import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Stack } from '@mui/material';
import { CustomButton, Text } from '../../shared/uiComponents';
import { dateNormalizer, errorNormalizer } from '../../shared/Helpers/functions';
import { AdminHours } from '../../redux/API/ClientAPIHelpers/adminHours';
import { UserSessionAllocationMonthlyDetailsProperties } from '../../redux/API/userAPIProperties';
import { useSelector } from '../../redux/store';

const AdminHoursUsage = ({ userId }: { userId: string }) => {
	const allocations = useSelector((state) => state.userSessionAllocation.userActiveAllocations);
	const month = dayjs().format('YYYY-MM');
	const [usage, setUsage] = useState<UserSessionAllocationMonthlyDetailsProperties | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const requestId = useRef(0);
	const loadUsage = useCallback(() => {
		requestId.current += 1;
		const request = requestId.current;
		setUsage(null);
		setError(false);
		setLoading(true);
		AdminHours.getUsage(userId, dateNormalizer(dayjs(`${month}-01`)))
			.then((result) => {
				if (request === requestId.current) setUsage(result);
			})
			.catch((requestError) => {
				if (request === requestId.current) {
					setError(true);
					errorNormalizer(requestError);
				}
			})
			.finally(() => {
				if (request === requestId.current) setLoading(false);
			});
	}, [userId, month]);
	const invalidateRequest = useCallback(() => { requestId.current += 1; }, []);
	useEffect(() => {
		loadUsage();

		return invalidateRequest;
	}, [loadUsage, allocations, invalidateRequest]);

	return (
		<div className="marginBottom16">
			<Stack spacing={2}>
				<Text title={`Admin Hours — ${dayjs(`${month}-01`).format('MMMM YYYY')}`} type="h6" fontWeight="semiBold" />
				{usage && !loading && !error ? (
					<Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
						{[
							{ label: 'Allocated', hours: usage.allocatedHoursPerMonth },
							{ label: 'Used', hours: usage.usedHoursThisMonth },
							{ label: 'Remaining', hours: usage.remainingHoursThisMonth },
						].map(({ label, hours }) => (
							<Stack key={label} spacing={0.5}>
								<Text title={label} type="psm" />
								<Text title={`${Number(hours.toFixed(2))} ${hours === 1 ? 'hour' : 'hours'}`} type="psm" fontWeight="semiBold" />
							</Stack>
						))}
					</Stack>
				) : (
					<Text
						title={loading ? 'Loading usage…' : error ? 'Unable to load Admin Hours usage.' : 'No active Admin Hours allocation.'}
						type="psm"
					/>
				)}
				{error && <CustomButton title="Retry" onClick={loadUsage} buttonType="outlined" />}
			</Stack>
		</div>
	);
};

export default AdminHoursUsage;
